import * as React from 'react';
import { Calendar, momentLocalizer, Event as BigCalendarEvent } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import 'moment/locale/fr';

declare const I18n: any;

const localizer = momentLocalizer(moment);

type CalendarEvent = BigCalendarEvent & {
  id: number;
  colorIndex?: number;
  resource?: any;
};

type Props = {
  events: CalendarEvent[];
  height?: string;
  onSelectEvent?: (event: CalendarEvent) => void;
};

const EventCalendar: React.FunctionComponent<Props> = ({
  events,
  height = '600px',
  onSelectEvent
}) => {
  const currentLocale = I18n.locale === 'fr' ? 'fr' : 'en';
  moment.locale(currentLocale);
  
  const formatCalendarLabel = (date: Date, format: string) => {
    const formatted = moment(date).locale(currentLocale).format(format);
    const withoutTrailingPeriod = formatted.replace(/\.$/, '');

    return withoutTrailingPeriod.charAt(0).toUpperCase() + withoutTrailingPeriod.slice(1);
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    const colors = ['#E70880', '#42C0C6', '#8DC862'];
    const eventIndex = events.indexOf(event);
    const colorIndex = event.colorIndex ?? eventIndex % 3;
    
    return {
      style: {
        backgroundColor: colors[colorIndex],
        borderRadius: '12px',
        color: 'white'
      }
    };
  };

  // Custom component for month date header
  const CustomDateHeader = ({ date, label }: { date: Date; label: string }) => {
    const isToday = moment(date).isSame(moment(), 'day');
    
    return (
      <div style={{ 
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '5px'
      }}>
        {isToday && (
          <i 
            className="fa-solid fa-circle" 
            style={{
              position: 'absolute',
              fontSize: '32px',
              color: '#813091',
              opacity: 0.8,
              zIndex: 0
            }}
          />
        )}
        <span style={{ position: 'relative', zIndex: 1, color: isToday ? "white" : "#813091"}}>
          {label}
        </span>
      </div>
    );
  };

  return (
    <div style={{ height }}>
      <Calendar
        localizer={localizer}
        culture={currentLocale}
        events={events}
        startAccessor="start"
        endAccessor="end"
        eventPropGetter={eventStyleGetter}
        onSelectEvent={onSelectEvent}
        views={['month', 'agenda']}
        messages={{
          today: I18n.t('models.event.calendar.today'),
          previous: I18n.t('models.event.calendar.previous'),
          next: I18n.t('models.event.calendar.next'),
          month: I18n.t('models.event.calendar.month'),
          agenda: I18n.t('models.event.calendar.agenda'),
          date: I18n.t('models.event.attributes.date'),
          time: I18n.t('models.event.attributes.time'),
          event: I18n.t('models.event.attributes.event'),
          noEventsInRange: I18n.t('models.event.messaging.empty_state')
        }}
        formats={{
          weekdayFormat: (date: Date) => formatCalendarLabel(date, 'ddd'),
          monthHeaderFormat: (date: Date) => formatCalendarLabel(date, 'MMMM YYYY')
        }}
        components={{
          month: {
            dateHeader: CustomDateHeader
          }
        }}
      />
    </div>
  );
};

export default EventCalendar;